/* ═══════════════════════════════════════════════════════════════
   STARDUST — materials layer behaviour
   preloader ribs · medallion scroll-flip · brand glyph swap
   Runs before main.js so the preloader can gate the hero.
   ═══════════════════════════════════════════════════════════════ */
(() => {
  "use strict";

  const reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
  const $  = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];

  /* ── 1 · PRELOADER ─────────────────────────────────────────────
     A sheet of reeded glass over the hero film, which main.js has already
     started. We do not touch the video: it plays behind the glass, visibly
     refracted, and the ribs part to hand it over sharp. Short by design —
     main.js gives up on the film after 4s, so this must be well clear.   */

  const preload = $("#preload");

  const buildRibs = () => {
    const host = $("#preloadRibs");
    if (!host) return [];
    const n = Math.max(16, Math.min(90, Math.round(innerWidth / 22)));
    const frag = document.createDocumentFragment();
    const ribs = [];
    for (let i = 0; i < n; i++) {
      const r = document.createElement("span");
      r.className = "preload__rib";
      frag.appendChild(r);
      ribs.push(r);
    }
    host.appendChild(frag);
    return ribs;
  };

  const partRibs = (ribs) => {
    if (!ribs.length) return Promise.resolve();
    const mid = (ribs.length - 1) / 2;
    const maxDelay = 0.16;
    return new Promise((resolve) => {
      ribs.forEach((r, i) => {
        const d = (1 - Math.abs(i - mid) / (mid || 1)) * maxDelay;
        r.style.transition =
          `transform .5s cubic-bezier(.66,0,.16,1) ${d}s, opacity .35s linear ${d + .12}s`;
        requestAnimationFrame(() => {
          r.style.transform = "scaleX(0)";
          r.style.opacity = "0";
        });
      });
      setTimeout(resolve, 520 + maxDelay * 1000);
    });
  };

  const wait = (ms) => new Promise((r) => setTimeout(r, ms));

  const runPreload = () => {
    if (!preload) return;
    if (reduce) { preload.classList.add("is-gone"); document.dispatchEvent(new CustomEvent("stardust:preloaded")); return; }
    const ribs = buildRibs();

    // hold for the display font and a short beat, never past 1.6s
    const font = document.fonts ? document.fonts.load('1em "TAN Angleton"').catch(() => {}) : null;
    Promise.race([Promise.all([font, wait(350)]), wait(900)]).then(async () => {
      preload.classList.add("is-open");   // blur clears as the flutes fold
      await partRibs(ribs);
      preload.classList.add("is-gone");
      document.dispatchEvent(new CustomEvent("stardust:preloaded"));
    });
  };

  /* ── 3 · BRAND GLYPH ───────────────────────────────────────────
     Replace the generic 4-point star with the medallion's quatrefoil
     wherever it's used as a bullet or divider.                     */

  /* lifted verbatim from the real back-face artwork (path 4), so the bullet
     glyph and the medallion badge are the identical shape */
  const QUATREFOIL =
    "M1215.7 784.3a229.3 229.3 0 0 0-431.4 0 229.3 229.3 0 0 0 0 431.4" +
    "a229.3 229.3 0 0 0 431.4 0 229.3 229.3 0 0 0 0-431.4";

  const swapGlyphs = () => {
    $$(".star-glyph, .scrollcue__star").forEach((svg) => {
      svg.setAttribute("viewBox", "0 0 2000 2000");
      const p = svg.querySelector("path");
      if (p) p.setAttribute("d", QUATREFOIL);
    });
  };


  /* ── 4 · RIB-WIPE REVEALS ──────────────────────────────────────
     Glass ribs over each frame, sweeping off left to right as it enters.
     Rides on top of main.js's clip-path wipe rather than replacing it.
     Skipped on coarse pointers: a dozen live backdrop-filters per frame
     is the single most expensive thing on this page. */

  const initRibWipes = () => {
    if (reduce) return;
    if (!window.gsap || !window.ScrollTrigger) return;

    const frames = $$(".reveal-frame");
    if (!frames.length) return;

    frames.forEach((frame) => {
      // flutes live inside the clip so they reveal with the image, not over its padding
      const host = frame.querySelector(".frame__clip") || frame;
      if (getComputedStyle(host).position === "static") host.style.position = "relative";
      const n = Math.max(8, Math.min(40, Math.round(host.offsetWidth / 22)));
      const wrap = document.createElement("div");
      wrap.className = "ribwipe";
      wrap.setAttribute("aria-hidden", "true");
      const ribs = [];
      for (let i = 0; i < n; i++) {
        const r = document.createElement("span");
        r.className = "ribwipe__r";
        wrap.appendChild(r);
        ribs.push(r);
      }
      host.appendChild(wrap);

      gsap.to(ribs, {
        scaleX: 0,
        opacity: 0,
        // same motion as the preloader: blur clears, flutes fold from the centre
        duration: .78,
        ease: "power3.inOut",
        stagger: { amount: .35, from: "center" },
        scrollTrigger: { trigger: frame, start: "top 82%", once: true },
        onStart: () => wrap.classList.add("is-open"),
        onComplete: () => wrap.remove()   // drop the backdrop-filter once spent
      });
    });
  };

  /* ── boot ──────────────────────────────────────────────────── */
  const boot = () => { runPreload(); swapGlyphs(); initRibWipes(); };
  if (document.readyState === "loading")
    document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();
